import React from 'react';
import { X, ShoppingCart, ArrowRight } from 'lucide-react';

interface CartItem {
  id: number;
  display_name: string;
  display_price: number;
  original_price: number;
  display_image: string;
  category: {
    name: string;
  };
  is_mystery_card: boolean;
  mystery_price: number;
  quantity?: number;
  quantity_available?: number;
}

interface CartOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onCheckout: () => void;
  onRemoveItem: (itemId: number) => void; // Required remove functionality
  onUpdateQuantity: (itemId: number, newQuantity: number) => void; // New quantity update functionality
}

export default function CartOverlay({ 
  isOpen, 
  onClose, 
  cartItems, 
  onCheckout, 
  onRemoveItem,
  onUpdateQuantity
}: CartOverlayProps) {
  if (!isOpen) return null;

  const totalPrice = cartItems.reduce((sum, item) => sum + (item.display_price * (item.quantity || 1)), 0);
  const totalSavings = cartItems.reduce((sum, item) => {
    const originalPrice = item.original_price || item.display_price;
    const currentPrice = item.display_price;
    return sum + ((originalPrice - currentPrice) * (item.quantity || 1));
  }, 0);

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-md bg-gradient-to-b from-background via-background to-background/95 shadow-2xl z-[70] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-gradient-to-r from-primary/10 to-secondary/10">
          <h2 className="text-xl font-semibold text-white">🛒 Shopping Cart</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-cosmic-secondary/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-cosmic-primary" />
          </button>
        </div>

        {/* Cart Items - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3 cart-scroll" style={{ 
          scrollbarWidth: 'thin', 
          scrollbarColor: '#8B5CF6 #1F2937' 
        }}>
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 text-6xl">🛒</div>
              <p className="text-cosmic-primary">Your cosmic cart is empty</p>
              <p className="text-sm text-muted-foreground mt-2">Start swiping to discover amazing products!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-4 p-4 bg-gradient-to-r from-cosmic-card/80 to-cosmic-secondary/20 rounded-xl backdrop-blur-sm hover:from-cosmic-card to-cosmic-secondary/30 transition-all duration-300">
                  <div className="relative">
                    <img 
                      src={item.display_image} 
                      alt={item.display_name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    {item.is_mystery_card && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-secondary to-mystery-glow rounded-full flex items-center justify-center">
                        <span className="text-xs">✨</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-white text-sm leading-tight">{item.display_name}</h3>
                    <p className="text-xs text-cosmic-primary/70 mt-1">{item.category.name}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-cosmic-primary font-bold text-sm">₹{item.display_price}</span>
                      {item.original_price && item.original_price > item.display_price && (
                        <>
                          <span className="text-xs text-muted-foreground line-through">₹{item.original_price}</span>
                          <span className="text-xs bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 py-0.5 rounded-full">
                            Save ₹{item.original_price - item.display_price}
                          </span>
                        </>
                      )}
                    </div>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-2 mt-3">
                      <span className="text-xs text-gray-400">Qty:</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => onUpdateQuantity(item.id, Math.max(1, (item.quantity || 1) - 1))}
                          className="w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
                          disabled={!item.quantity || item.quantity <= 1}
                        >
                          <span className="text-white text-sm">-</span>
                        </button>
                        <span className="text-white font-medium text-sm min-w-[20px] text-center">
                          {item.quantity || 1}
                          {(item.quantity_available || 1) > 1 && (
                            <span className={`text-xs block ${(item.quantity || 1) >= (item.quantity_available || 1) ? 'text-yellow-400' : 'text-gray-400'}`}>
                              /{item.quantity_available}
                              {(item.quantity || 1) >= (item.quantity_available || 1) && ' (Max)'}
                            </span>
                          )}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item.id, (item.quantity || 1) + 1)}
                          disabled={(item.quantity || 1) >= (item.quantity_available || 1)}
                          className="w-6 h-6 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 rounded-full flex items-center justify-center transition-colors"
                        >
                          <span className="text-white text-sm">+</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => onRemoveItem(item.id)}
                    className="p-2 hover:bg-red-500/20 rounded-full transition-colors group"
                  >
                    <X className="w-4 h-4 text-red-400 group-hover:text-red-300" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 space-y-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">✨ Total Savings:</span>
              <span className="text-green-400 font-bold">₹{totalSavings.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <span className="text-white">🚀 Total:</span>
              <span className="text-cosmic-primary font-bold">₹{totalPrice.toLocaleString()}</span>
            </div>
            <button 
              onClick={onCheckout}
              className="w-full bg-gradient-to-r from-cosmic-primary via-primary to-secondary text-white font-bold py-4 px-6 rounded-xl hover:from-cosmic-primary/90 hover:via-primary/90 hover:to-secondary/90 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              <span>⚡ Proceed to Checkout</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

